import { useCallback } from 'react';

import { formatDate } from '../lib/format';
import { useAdminData } from '../lib/use-admin-data';
import {
  fetchAdminDevices,
  fetchAdminGeo,
  fetchAdminVisitors,
  type AdminFilters,
  type DeviceBreakdown,
  type GeoBreakdown,
  type VisitorStat,
} from '../services/api';
import { BreakdownBars } from './BreakdownBars';
import { Feedback } from './Feedback';

/**
 * Aba PÚBLICO — de onde vêm os acessos e com o quê.
 *
 * A geolocalização é resolvida em segundo plano, uma vez por IP (ver
 * src/lib/geoip.ts na API). Acessos ainda não resolvidos aparecem como
 * "Não identificado" em vez de sumirem: um mapa que esconde o que não sabe
 * passa uma falsa sensação de cobertura total.
 */

interface AdminAudienceTabProps {
  filters: AdminFilters;
}

export function AdminAudienceTab({ filters }: AdminAudienceTabProps) {
  const geoLoader = useCallback(() => fetchAdminGeo(filters), [filters]);
  const devicesLoader = useCallback(() => fetchAdminDevices(filters), [filters]);
  const visitorsLoader = useCallback(() => fetchAdminVisitors(filters), [filters]);

  const geo = useAdminData<GeoBreakdown>(geoLoader);
  const devices = useAdminData<DeviceBreakdown>(devicesLoader);
  const visitors = useAdminData<{ visitors: VisitorStat[] }>(visitorsLoader);

  const error = geo.error ?? devices.error ?? visitors.error;
  if (error) return <Feedback kind="error">{error}</Feedback>;
  if (!geo.data || !devices.data) return <p className="page-loading">Carregando…</p>;

  return (
    <div className="admin-tab">
      <div className="admin-grid">
        <BreakdownBars
          title="Países"
          items={geo.data.countries.map((row) => ({
            label: row.country,
            value: row.requests,
            detail: formatAddressCount(row.visitors),
          }))}
        />
        <BreakdownBars
          title="Cidades"
          items={geo.data.cities.map((row) => ({
            label: [row.city, row.region, row.country].filter(Boolean).join(' · '),
            value: row.requests,
            detail: formatAddressCount(row.visitors),
          }))}
          emptyMessage="Nenhuma cidade resolvida ainda."
        />
        <BreakdownBars
          title="Operadoras e provedores"
          description="Útil para separar gente de datacenter: nuvem em massa costuma ser robô."
          items={geo.data.organizations.map((row) => ({
            label: row.organization ?? 'Não identificado',
            value: row.requests,
          }))}
        />
        {geo.data.loadTestOrigins.length > 0 && (
          <BreakdownBars
            title="Origens do teste de carga"
            description="Declarada por cada runner — é o que separa as regiões no teste distribuído."
            items={geo.data.loadTestOrigins.map((row) => ({
              label: row.origin ?? 'Sem origem',
              value: row.requests,
              detail: formatAddressCount(row.visitors),
            }))}
          />
        )}
      </div>

      <div className="admin-grid">
        <BreakdownBars title="Navegadores" items={devices.data.browsers.map(toBreakdownItem)} />
        <BreakdownBars
          title="Sistemas operacionais"
          items={devices.data.operatingSystems.map(toBreakdownItem)}
        />
        <BreakdownBars
          title="Tipo de aparelho"
          items={devices.data.devices.map((row) => ({
            ...toBreakdownItem(row),
            label: DEVICE_LABEL[row.label] ?? row.label,
          }))}
        />
        <BreakdownBars
          title="De onde vieram (referer)"
          items={devices.data.referers.map(toBreakdownItem)}
          emptyMessage="Nenhum acesso com referer no período."
        />
      </div>

      <section className="admin-section">
        <h3 className="admin-section__title">Visitantes mais ativos</h3>
        <p className="admin-section__description">
          Endereços com mais requisições no período. Um único IP concentrando tudo é abuso ou teste
          — a coluna de origem diz qual.
        </p>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Endereço</th>
                <th scope="col">Origem</th>
                <th scope="col">Requisições</th>
                <th scope="col">Erros</th>
                <th scope="col">Rotas</th>
                <th scope="col">Último acesso</th>
              </tr>
            </thead>
            <tbody>
              {(visitors.data?.visitors ?? []).map((visitor) => (
                <tr key={visitor.ip ?? 'desconhecido'}>
                  <td>
                    <code>{visitor.ip ?? '—'}</code>
                    {visitor.isSynthetic && <span className="tag tag--synthetic">teste</span>}
                  </td>
                  <td>
                    {[visitor.city, visitor.country].filter(Boolean).join(', ') ||
                      'Não identificado'}
                    {visitor.organization && (
                      <span className="data-table__muted"> · {visitor.organization}</span>
                    )}
                  </td>
                  <td className="data-table__number">{visitor.requests.toLocaleString('pt-BR')}</td>
                  <td className="data-table__number">{visitor.errors.toLocaleString('pt-BR')}</td>
                  <td className="data-table__number">{visitor.routes}</td>
                  <td>{formatDate(visitor.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/** "1 endereço" / "2 endereços" — sem o plural quebrado do singular. */
function formatAddressCount(total: number): string {
  return `${total.toLocaleString('pt-BR')} ${total === 1 ? 'endereço' : 'endereços'}`;
}

const DEVICE_LABEL: Record<string, string> = {
  mobile: 'Celular',
  tablet: 'Tablet',
  desktop: 'Computador',
  bot: 'Robô/ferramenta',
  unknown: 'Não identificado',
};

/** Converte um agrupamento da API no formato do ranking em barras. */
function toBreakdownItem(row: { label: string; requests: number; visitors: number }) {
  return {
    label: row.label,
    value: row.requests,
    detail: formatAddressCount(row.visitors),
  };
}
